import { useCallback } from 'react';
import {
	useSetTradeFormBaseSize,
	useSetTradeFormQuoteSize,
} from 'src/components/TradeForm/hooks/useSetTradeFormInputState';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import { SizeUnit } from 'src/components/TradeForm/SizeInput/types';
import { TradeFormHandleSizeInputChange } from 'src/components/TradeForm/types';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { capStringFractionDigits } from '@velocity-exchange/common/format';

interface Params {
	sizeDigits: number;
}

export default function useTradeFormHandleSizeInputChange({
	sizeDigits,
}: Params) {
	const setTradeFormMaxLeverageSelected = useVelocityStore(
		(s) => s.setTradeFormMaxLeverageSelected
	);
	const setFormQuoteSize = useSetTradeFormQuoteSize();
	const setFormBaseSize = useSetTradeFormBaseSize();

	const [leadSide] = useSizeInputSource();

	const set = useVelocityStore((s) => s.set);
	const storeLeadSideInTradeForm = useCallback(
		(side: SizeUnit) => {
			set((s) => {
				s.tradeForm.leadSide = side;
			});
		},
		[set]
	);

	return useCallback<TradeFormHandleSizeInputChange>(
		(value, { sizeUnit } = {}) => {
			// Turn off max leverage for user-initiated change.
			setTradeFormMaxLeverageSelected(false);

			const baseSizeRoundedToStepSize = capStringFractionDigits(value, {
				maxFractionDigits: sizeDigits,
			});

			// Some updates only operate on either quote or base. So we need to
			// temporarily force one or the other for any dependent logic to run
			// correctly, then switch back to the true lead side after.
			if (sizeUnit) {
				storeLeadSideInTradeForm(sizeUnit);

				if (sizeUnit === 'base') {
					setFormBaseSize(baseSizeRoundedToStepSize);
				} else {
					setFormQuoteSize(value);
				}

				setTimeout(() => {
					storeLeadSideInTradeForm(leadSide);
				}, 1);

				return;
			}

			storeLeadSideInTradeForm(leadSide);

			if (leadSide === 'base') {
				setFormBaseSize(baseSizeRoundedToStepSize);
			} else {
				setFormQuoteSize(value);
			}
		},
		[
			setFormBaseSize,
			setFormQuoteSize,
			storeLeadSideInTradeForm,
			setTradeFormMaxLeverageSelected,
			leadSide,
			sizeDigits,
		]
	);
}
